"use client"
import type { OperationContext as OperationContextType } from "@opensea/graphql"
import { createContext, useContext } from "react"

const OperationContext = createContext<
  Partial<OperationContextType> | null | undefined
>(null)

type Props = {
  children: React.ReactNode
  context: Partial<OperationContextType> | undefined
}

/**
 * Provides a way to apply a shared `OperationContext` over a set of components,
 * e.g. wrapping a whole page.
 *
 * Useful for setting common query behavior such as TTLs, cache policies,
 * or fetch options that should apply to all GraphQL queries within the scope.
 *
 * @example
 * ```tsx
 * <OperationContextProvider context={{ requestPolicy: "cache-first", ttl: 5000 }}>
 *   <Page />
 * </OperationContextProvider>
 * ```
 */
export function OperationContextProvider({ children, context }: Props) {
  return (
    <OperationContext.Provider value={context}>
      {children}
    </OperationContext.Provider>
  )
}

export function useOperationContext() {
  return useContext(OperationContext)
}
